import { RouterLink } from '@angular/router';
import { isVaultNotActive, VAULT_NOT_ACTIVE_MESSAGE } from '../../../keystore/services/keystore-error';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { CustomMaterialModule } from 'src/app/customModules/app-material-module';
import { ChangeDetectorRef, Component, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { KeystoreApiService } from '../../../keystore/services/keystore-api.service';
import { KeyPairMetadata } from '../../../keystore/models/keystore-api.models';
import { DIDEntry } from '../../../did-manager/models/did-entry.model';
import { OrganisationService } from 'src/app/services/dataServices/organisation-service/organisationservice';
@Component({ selector: 'app-onboarding-identity-dialog', standalone: true, imports: [RouterLink, CommonModule, ReactiveFormsModule, CustomMaterialModule], templateUrl: './onboarding-identity-dialog.component.html', styleUrls: ['./onboarding-identity-dialog.component.scss'] })
export class OnboardingIdentityDialogComponent {
  readonly base = '/proxy/api/v0/cx-features/did-manager/onboarding';
  readonly form = this.fb.group({ label: ['', [Validators.required, Validators.maxLength(100)]], hosted: [true], domain: [''], algorithm: ['ED25519'], importKey: [false], privateKeyPem: [''], verificationMethodId: [''], services: this.fb.array<FormGroup>([]) });
  get services() { return this.form.controls.services; }
  addService() {
    this.services.push(this.fb.group({
      label: ['', [Validators.required, Validators.maxLength(100)]],
      type: ['', [Validators.required, Validators.pattern('[A-Za-z][A-Za-z0-9]*')]],
      serviceEndpoint: ['', [Validators.required, Validators.maxLength(2048), Validators.pattern('https?://[^\\s]+')]]
    }));
  }
  hostDomain = '';
  document: any = null;
  preview: { algorithm: string; publicKeyPem: string } | null = null;
  previewPem = '';
  busy = false;
  error = '';
  get vaultNotActive(): boolean { return this.error === VAULT_NOT_ACTIVE_MESSAGE; }
  progress = '';
  storedKey: KeyPairMetadata | null = null;
  result: { entry: DIDEntry; didDocument: any; publicationStatus: string } | null = null;
  constructor(private cdr: ChangeDetectorRef, private fb: FormBuilder, private http: HttpClient, private keys: KeystoreApiService, private organisations: OrganisationService,
    private ref: MatDialogRef<OnboardingIdentityDialogComponent>, @Inject(MAT_DIALOG_DATA) public data: { organisationId: number; mode: 'create' | 'import' }) {
    if (data.mode === 'import') this.form.patchValue({ importKey: true });
    this.http.get<{hostDomain:string}>(this.base+'/configuration').subscribe({next: config => this.hostDomain=config.hostDomain, error: () => this.error='DID-Konfiguration konnte nicht geladen werden.'});
  }
  async readKey(event: Event) {
    const file=(event.target as HTMLInputElement).files?.[0]; if(!file)return;
    if(file.size>65536){this.error='Die Schlüsseldatei ist zu groß.';return;}
    try{this.form.controls.privateKeyPem.setValue(await file.text());await this.inspect();}catch{this.error='Datei konnte nicht gelesen werden.';}
  }
  async readDocument(event: Event) {
    const file=(event.target as HTMLInputElement).files?.[0];if(!file)return;
    if(file.size>262144){this.error='Das DID-Dokument ist zu groß.';return;}
    try{this.document=JSON.parse(await file.text());const method=this.document.assertionMethod?.[0];this.form.controls.verificationMethodId.setValue(typeof method==='string'?method:method?.id || '');this.error='';}
    catch{this.document=null;this.error='Bitte ein gültiges DID-Dokument im JSON-Format wählen.';}
  }
  async inspect() {
    this.preview=null;this.error='';const pem=this.form.controls.privateKeyPem.value || '';if(!pem)return;
    this.busy=true;
    try{this.preview=await firstValueFrom(this.keys.inspectPrivateKey(this.data.organisationId,pem));this.previewPem=pem;}
    catch(error:any){this.error=error.error?.message || 'Ungültiger privater Schlüssel.';}
    finally{this.busy=false;this.cdr.markForCheck();}
  }
  get canCreate() {
    const raw=this.form.getRawValue();
    return this.form.valid && (raw.hosted || !!raw.domain) && (!raw.importKey || (!!this.preview && this.previewPem===raw.privateKeyPem))
      && (this.data.mode!=='import' || (!!this.document && !!raw.verificationMethodId));
  }
  async create() {
    if(this.busy || !this.canCreate)return;this.busy=true;this.error='';this.ref.disableClose=true;
    try{
      const raw=this.form.getRawValue();
      if(!this.storedKey){
        this.progress='Schlüssel sicher speichern …';
        this.storedKey=await firstValueFrom(raw.importKey
          ?this.keys.importKeyPair(this.data.organisationId,{label:raw.label!,privateKeyPem:raw.privateKeyPem!})
          :this.keys.generateKeyPair(this.data.organisationId,{label:raw.label!,algorithm:raw.algorithm as 'RSA'|'ED25519'}));
      }
      this.progress='Digitale Identität erstellen …';
      this.result=await firstValueFrom(this.http.post<any>(this.base+(this.data.mode==='import'?'/import':'/identities'),{
        service:raw.services,organisationId:this.data.organisationId,organisationName:this.organisations.activeOrganisation?.organisationName || this.organisations.activeOrganisation?.name || '',label:raw.label,hosted:raw.hosted,domain:raw.domain,keyPairId:this.storedKey.id,
        ...(this.data.mode==='import'?{didDocument:this.document,verificationMethodId:raw.verificationMethodId}:{})
      }));
      this.form.controls.privateKeyPem.reset();this.previewPem='';
      this.progress=this.result?.publicationStatus==='DOWNLOAD_REQUIRED'?'DID-Dokument auf Ihrem Webserver veröffentlichen.':'Ihre digitale Identität steht zur Verfügung.';
    }catch(error:any){this.error=isVaultNotActive(error) ? VAULT_NOT_ACTIVE_MESSAGE : error.error?.detail || error.error?.message || 'Die Identität konnte nicht erstellt werden. Bereits gespeicherte Schlüssel werden beim Wiederholen wiederverwendet.';}
    finally{this.busy=false;this.ref.disableClose=false;this.cdr.markForCheck();}
  }
  download() {if(!this.result)return;const url=URL.createObjectURL(new Blob([JSON.stringify(this.result.didDocument,null,2)],{type:'application/json'}));const a=document.createElement('a');a.href=url;a.download='did.json';a.click();URL.revokeObjectURL(url);}
  finish() {if(this.result)this.ref.close({entry:this.result.entry,keyPairId:this.storedKey!.id});}
}
